/**
 * AnalysisTab — Search moments and prompt-based video analysis.
 *
 * Two sub-tabs:
 *   - Search: full-text search across video moments (browse + query)
 *   - Analyze: prompt-based video analysis using selected AI provider/model
 */

import { useState, useEffect, useCallback } from 'react'
import { Film, Send, Loader2, Sparkles, Clock, ChevronDown, Search, Play, Hourglass, RefreshCw, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../../services/api'
import { isVideoAsset } from '../../utils/mediaType'
import MediaPreviewModal from '../MediaPreviewModal'
import { useMediaPreview } from '../../hooks/useMediaPreview'
import { apiErrorDetail } from '../../utils/dataValidator'

interface VideoAsset {
  id: number
  original_filename: string
  file_type: string
  duration: number | null
  file_size: number
  thumbnail_path: string | null
}

interface AnalysisResult {
  prompt: string
  response: string
  tokens_used: number
  processing_time: number
  asset_filename: string
  provider: string
  model_used: string
}

interface ThemeMoment {
  id: number
  asset_id: number | null
  asset_filename: string | null
  scene_index: number
  start_time: number | null
  end_time: number | null
  title: string | null
  visual_description: string | null
  thumbnail_url: string | null
}

interface Theme {
  label: string
  intent: string
  moment_count: number
  moments: ThemeMoment[]
}

interface VideoThemes {
  asset_id: number
  asset_filename: string | null
  total_moments: number
  themes: Theme[]
  unclustered_reason: string | null
}

interface ThemesResponse {
  videos: VideoThemes[]
  total_moments: number
  assets_analyzed: number
}

const THEMES_PROMPT = 'Generate themes and tags for this footage'

interface ProviderInfo {
  id: string
  label: string
  available: boolean
  model_count: number
}

interface ModelInfo {
  id: string
  label: string
  full_name: string
  capabilities: string[]
  default_for: string[]
}

interface MomentItem {
  id: number
  asset_id: number | null
  scene_index: number
  start_time: number | null
  end_time: number | null
  title: string | null
  visual_description: string | null
  audio_description: string | null
  thumbnail_url: string | null
  tags: string[] | null
  notes: string | null
  asset_filename: string | null
  asset_file_type: string | null
  asset_duration: number | null
}

interface AnalysisTabProps {
  projectIds: string[]
}

type SubTab = 'search' | 'analyze'

const SUGGESTED_PROMPTS = [
  { label: 'Generate themes and tags', prompt: 'Generate themes and tags for this footage' },
  { label: 'Summarize this video', prompt: 'Summarize this video' },
  { label: 'Key moments and highlights', prompt: 'What are the key moments and highlights?' },
  { label: 'Scenes and timestamps', prompt: 'Break down the video by scenes and timestamps' },
  { label: 'Visual style and cinematography', prompt: 'Describe the visual style and cinematography' },
  { label: 'Subjects and objects', prompt: 'What subjects and objects appear in this video?' },
  { label: 'Best audience or use case', prompt: 'Which audience or use case is this footage best for?' },
]

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--:--'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatTime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatTotalDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} sec`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`
}

export default function AnalysisTab({ projectIds }: AnalysisTabProps) {
  const { preview, closePreview, openFromMoment } = useMediaPreview()
  const [subTab, setSubTab] = useState<SubTab>('search')

  // --- Analyze sub-tab state ---
  const [assets, setAssets] = useState<VideoAsset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null)
  const [prompt, setPrompt] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  // --- Themes clustering state ---
  const [themeVideos, setThemeVideos] = useState<VideoThemes[]>([])
  const [themesLoading, setThemesLoading] = useState(false)
  const [themesMessage, setThemesMessage] = useState<string | null>(null)

  // --- AI provider/model state ---
  const [providers, setProviders] = useState<ProviderInfo[]>([])
  const [models, setModels] = useState<ModelInfo[]>([])
  const [selectedProvider, setSelectedProvider] = useState('gemini')
  const [selectedModel, setSelectedModel] = useState('flash')
  const [providerError, setProviderError] = useState<string | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)

  // --- Search sub-tab state ---
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<MomentItem[]>([])
  const [searchTotal, setSearchTotal] = useState(0)
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const selectedAsset = assets.find(a => a.id === selectedAssetId) || null

  // Fetch video assets for Analyze sub-tab
  const fetchAssets = useCallback(async () => {
    if (projectIds.length === 0) {
      setAssets([])
      setSelectedAssetId(null)
      return
    }
    try {
      setAssetsLoading(true)
      const results = await Promise.all(
        projectIds.map(pid =>
          apiClient.get('/assets/', { params: { project_id: pid, limit: 200 } })
        )
      )
      const allAssets: VideoAsset[] = []
      for (const res of results) {
        for (const asset of res.data.assets) {
          if (isVideoAsset(asset)) {
            allAssets.push(asset)
          }
        }
      }
      setAssets(allAssets)
      setSelectedAssetId(allAssets.length > 0 ? allAssets[0].id : null)
    } catch {
      toast.error('Failed to load video assets')
    } finally {
      setAssetsLoading(false)
    }
  }, [projectIds])

  useEffect(() => { fetchAssets() }, [fetchAssets])

  // Fetch moments for Search sub-tab (browse mode on mount)
  const fetchMoments = useCallback(async (query?: string) => {
    if (projectIds.length === 0) {
      setSearchResults([])
      setSearchTotal(0)
      return
    }
    try {
      setSearching(true)
      const params: Record<string, string | number> = {
        project_ids: projectIds.join(','),
        limit: 100,
      }
      if (query && query.trim()) {
        params.q = query.trim()
      }
      const res = await apiClient.get('/moments/', { params })
      setSearchResults(res.data.moments)
      setSearchTotal(res.data.total)
    } catch {
      toast.error('Failed to search moments')
    } finally {
      setSearching(false)
    }
  }, [projectIds])

  // Load moments on mount / project change (browse mode)
  useEffect(() => {
    fetchMoments()
    setSearchQuery('')
    setHasSearched(false)
  }, [fetchMoments])

  const fetchProviders = useCallback(async () => {
    try {
      setProviderError(null)
      const res = await apiClient.get('/ai/providers')
      const data: ProviderInfo[] = res.data
      setProviders(data)
      const available = data.filter(p => p.available)
      if (available.length === 0) {
        setProviderError('No AI providers are configured.')
      } else {
        setSelectedProvider(prev => available.find(p => p.id === prev) ? prev : available[0].id)
      }
    } catch {
      setProviderError('Failed to load AI providers.')
    }
  }, [])

  // Fetch AI providers on mount
  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  // Fetch models when provider changes (only show models that support video)
  useEffect(() => {
    if (!selectedProvider) return
    setModelError(null)
    apiClient.get(`/ai/providers/${selectedProvider}/models`)
      .then(res => {
        const data: ModelInfo[] = res.data
        const videoModels = data.filter(m => m.capabilities?.includes('video'))
        setModels(videoModels)
        const defaultModel = videoModels.find(m => m.default_for?.includes('analyze_video'))
        setSelectedModel(defaultModel?.id || videoModels[0]?.id || '')
        if (videoModels.length === 0) {
          setModelError('No video-capable models available for this provider.')
        }
      })
      .catch(() => {
        setModels([])
        setSelectedModel('')
        setModelError('Failed to load models for this provider.')
      })
  }, [selectedProvider])

  // Clear analysis result when video changes
  useEffect(() => { setResult(null) }, [selectedAssetId])

  // --- Analyze handlers ---

  const handleAnalyzeSubmit = async (submittedPrompt?: string) => {
    const finalPrompt = submittedPrompt || prompt
    if (!finalPrompt.trim() || !selectedAssetId || !selectedModel) return

    setAnalyzing(true)
    setResult(null)
    try {
      const res = await apiClient.post(`/analysis/${selectedAssetId}/ask`, {
        prompt: finalPrompt.trim(),
        provider: selectedProvider,
        model: selectedModel,
      })
      setResult({
        prompt: finalPrompt.trim(),
        response: res.data.response,
        tokens_used: res.data.tokens_used,
        processing_time: res.data.processing_time,
        asset_filename: selectedAsset?.original_filename || 'Unknown',
        provider: res.data.provider || selectedProvider,
        model_used: res.data.model_used || selectedModel,
      })
    } catch (err) {
      const detail = apiErrorDetail(err, 'Analysis failed')
      toast.error(detail)
    } finally {
      setAnalyzing(false)
    }
  }

  const handleSuggestedPrompt = (suggestedPrompt: string) => {
    setPrompt(suggestedPrompt)
    if (suggestedPrompt === THEMES_PROMPT) {
      runThemes()
      return
    }
    handleAnalyzeSubmit(suggestedPrompt)
  }

  const runThemes = async () => {
    if (projectIds.length === 0) {
      toast.error('Select a workspace first')
      return
    }
    setThemesLoading(true)
    setThemeVideos([])
    setThemesMessage(null)
    setResult(null)
    try {
      const res = await apiClient.post('/analysis/themes', {
        scope: { type: 'project', project_id: projectIds[0] },
      })
      const data: ThemesResponse = res.data
      setThemeVideos(data.videos)
      const anyThemes = data.videos.some(v => v.themes.length > 0)
      if (!anyThemes) {
        const firstReason = data.videos.find(v => v.unclustered_reason)?.unclustered_reason
        setThemesMessage(firstReason || 'No themes were produced.')
      }
    } catch (err) {
      const detail = apiErrorDetail(err, 'Theme clustering failed')
      toast.error(detail)
      setThemesMessage(detail)
    } finally {
      setThemesLoading(false)
    }
  }

  const handleAnalyzeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAnalyzeSubmit()
    }
  }

  // --- Search handlers ---

  const handleSearch = () => {
    setHasSearched(true)
    fetchMoments(searchQuery)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSearch()
    }
  }

  // Count distinct videos and total duration in search results
  const distinctVideoCount = new Set(searchResults.map(m => m.asset_id).filter(Boolean)).size
  const totalResultDuration = searchResults.reduce((sum, m) => {
    if (m.start_time !== null && m.end_time !== null) return sum + (m.end_time - m.start_time)
    return sum
  }, 0)

  if (projectIds.length === 0) {
    return (
      <div className="text-center py-16">
        <Sparkles className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Search & Analysis</h2>
        <p className="text-[var(--rq-text-soft)]">Select a workspace to search moments and analyze videos with AI</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="flex items-center gap-8 border-b border-white/10">
        {(['search', 'analyze'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`relative pb-3.5 text-base transition-colors flex items-center gap-2 ${
              subTab === tab
                ? 'text-[var(--rq-text)] font-semibold'
                : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] font-medium'
            }`}
          >
            {tab === 'search' && subTab === 'search' && (
              <RefreshCw className="w-4 h-4" />
            )}
            {tab === 'search' ? 'Search' : 'Analyze'}
            {subTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--brand-light)] rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Search sub-tab */}
      {subTab === 'search' && (
        <div className="space-y-5">
          {/* Search input */}
          <div className="flex items-stretch gap-3">
            <div className="flex-1 rounded-2xl border border-white/15 bg-black/20 shadow-[0_0_20px_rgba(0,0,0,0.25)] focus-within:shadow-[0_0_25px_rgba(47,107,255,0.2)] focus-within:border-white/35 transition-all overflow-hidden">
              <textarea
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search across video moments..."
                rows={3}
                disabled={searching}
                className="w-full bg-transparent text-[var(--rq-text)] text-base px-5 pt-4 pb-3 placeholder-[var(--rq-text-soft)] resize-none focus:outline-none disabled:opacity-50"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching}
              className="flex-shrink-0 w-12 h-12 self-end mb-0.5 rounded-xl bg-white/90 hover:bg-white text-[#10131a] flex items-center justify-center disabled:opacity-30 transition-colors"
              title="Search"
            >
              {searching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Results info bar */}
          {!searching && searchResults.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-[var(--rq-text-soft)]">
                <span className="flex items-center gap-1.5">
                  <Play className="w-3.5 h-3.5" />
                  {searchResults.length} / {searchTotal} moments
                  {distinctVideoCount > 0 && (
                    <span className="text-[var(--rq-text-soft)] mx-1">&middot;</span>
                  )}
                  {distinctVideoCount > 0 && (
                    <>{distinctVideoCount} video{distinctVideoCount !== 1 ? 's' : ''}</>
                  )}
                </span>
                {totalResultDuration > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Hourglass className="w-3.5 h-3.5" />
                    {formatTotalDuration(totalResultDuration)}
                  </span>
                )}
              </div>
              <span className="text-sm text-[var(--rq-text-soft)]">
                Sort by <span className="text-[var(--rq-text-soft)]">{hasSearched ? 'Relevance' : 'Recent upload'}</span>
              </span>
            </div>
          )}

          {/* Loading state */}
          {searching && (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-[var(--rq-blue)] mx-auto mb-3 animate-spin" />
              <p className="text-[var(--rq-text-soft)] text-sm">Searching moments...</p>
            </div>
          )}

          {/* Empty state after search */}
          {!searching && hasSearched && searchResults.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-[var(--rq-text-soft)] mx-auto mb-3" />
              <h3 className="text-[var(--rq-text)] font-medium mb-1">No results found</h3>
              <p className="text-[var(--rq-text-soft)] text-sm">Try a different search term</p>
            </div>
          )}

          {/* Empty state before any search and no moments */}
          {!searching && !hasSearched && searchResults.length === 0 && (
            <div className="text-center py-12">
              <Film className="w-12 h-12 text-[var(--rq-text-soft)] mx-auto mb-3" />
              <h3 className="text-[var(--rq-text)] font-medium mb-1">No moments yet</h3>
              <p className="text-[var(--rq-text-soft)] text-sm">Upload videos and run analysis to generate moments</p>
            </div>
          )}

          {/* Results grid */}
          {!searching && searchResults.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {searchResults.map(moment => (
                <div
                  key={moment.id}
                  className="rq-card overflow-hidden hover:border-white/25 transition-all"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-black/35">
                    {moment.thumbnail_url ? (
                      <>
                        <img
                          src={moment.thumbnail_url}
                          alt={moment.title || 'Clip thumbnail'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <button
                          onClick={() => openFromMoment(moment)}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white/90 hover:text-white hover:bg-black/85 flex items-center justify-center transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-10 h-10 text-[var(--rq-text-soft)]" />
                      </div>
                    )}

                    {/* Duration badge */}
                    {moment.start_time !== null && moment.end_time !== null && (
                      <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                        <Clock className="w-3 h-3" />
                        {formatTime(moment.end_time - moment.start_time)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-sm text-[var(--rq-text-soft)] line-clamp-2 leading-relaxed">
                      {moment.visual_description || moment.title || 'No description'}
                    </p>
                    {moment.asset_filename && (
                      <p className="text-xs text-[var(--rq-text-soft)] truncate mt-2">
                        {moment.asset_filename}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analyze sub-tab */}
      {subTab === 'analyze' && (
        <>
          {assetsLoading ? (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 text-[var(--rq-blue)] mx-auto mb-4 animate-spin" />
              <p className="text-[var(--rq-text-soft)]">Loading video assets...</p>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16">
              <Film className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No Videos Found</h2>
              <p className="text-[var(--rq-text-soft)]">Upload video assets to your workspace first, then come back to analyze them</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Two-column layout */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Left column — Video selector + Prompt input */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Video preview area */}
                  <div className="rq-card overflow-hidden">
                    {/* Video thumbnail / preview */}
                    <div className="aspect-video bg-black/35 flex items-center justify-center relative">
                      {selectedAsset ? (
                        <div className="w-full h-full flex items-center justify-center relative">
                          <Film className="w-16 h-16 text-[var(--rq-text-soft)]" />
                          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                            <span className="text-xs text-[var(--rq-text-soft)] bg-black/60 px-2 py-1 rounded truncate max-w-[60%]">
                              {selectedAsset.original_filename}
                            </span>
                            {selectedAsset.duration != null && (
                              <span className="text-xs text-[var(--rq-text-soft)] bg-black/60 px-2 py-1 rounded flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(selectedAsset.duration)}
                              </span>
                            )}
                          </div>
                          {/* Purple glow border when selected */}
                          <div className="absolute inset-0 border-2 border-[var(--rq-blue)]/40 rounded-t-xl pointer-events-none" />
                        </div>
                      ) : (
                        <div className="text-[var(--rq-text-soft)] text-sm">Select a video below</div>
                      )}
                    </div>

                    {/* Video selector dropdown */}
                    <div className="p-3 border-t border-white/10">
                      <div className="relative">
                        <button
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-[var(--rq-text)] transition-colors"
                        >
                          <span className="truncate">
                            {selectedAsset ? selectedAsset.original_filename : 'Select a video...'}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0 ml-2 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showDropdown && (
                          <div className="absolute z-20 mt-1 w-full bg-[var(--rq-surface-2)] border border-white/20 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {assets.map(asset => (
                              <button
                                key={asset.id}
                                onClick={() => {
                                  setSelectedAssetId(asset.id)
                                  setShowDropdown(false)
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/10 transition-colors ${
                                  asset.id === selectedAssetId ? 'bg-white/10' : ''
                                }`}
                              >
                                <Film className="w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-[var(--rq-text)] truncate">{asset.original_filename}</div>
                                  <div className="text-xs text-[var(--rq-text-soft)]">
                                    {formatDuration(asset.duration)} &middot; {formatFileSize(asset.file_size)}
                                  </div>
                                </div>
                                {asset.id === selectedAssetId && (
                                  <div className="w-2 h-2 rounded-full bg-[var(--brand-light)] flex-shrink-0" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Provider & Model selectors */}
                  <div className="rq-card p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-[var(--rq-text-soft)] mb-1 block">Provider</label>
                        <select
                          value={selectedProvider}
                          onChange={e => setSelectedProvider(e.target.value)}
                          disabled={analyzing || providers.filter(p => p.available).length === 0}
                          className="w-full bg-[#1a1a2e] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-[var(--rq-text)] disabled:opacity-50 focus:outline-none focus:border-white/35"
                        >
                          {providers.filter(p => p.available).length === 0 && (
                            <option value="" disabled>No providers</option>
                          )}
                          {providers.filter(p => p.available).map(p => (
                            <option key={p.id} value={p.id} className="bg-[#1a1a2e] text-white">{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-[var(--rq-text-soft)] mb-1 block">Model</label>
                        <select
                          value={selectedModel}
                          onChange={e => setSelectedModel(e.target.value)}
                          disabled={analyzing || models.length === 0}
                          className="w-full bg-[#1a1a2e] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-[var(--rq-text)] disabled:opacity-50 focus:outline-none focus:border-white/35"
                        >
                          {models.length === 0 && (
                            <option value="" disabled>No models</option>
                          )}
                          {models.map(m => (
                            <option key={m.id} value={m.id} className="bg-[#1a1a2e] text-white">{m.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {providerError && (
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <p className="text-xs text-amber-300">{providerError}</p>
                        <button
                          onClick={fetchProviders}
                          className="text-xs text-[var(--rq-blue)] hover:underline"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {modelError && (
                      <p className="mt-2 text-xs text-amber-300">{modelError}</p>
                    )}
                  </div>

                  {/* Prompt input */}
                  <div className="rq-card p-4">
                    <div className="relative">
                      <textarea
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        onKeyDown={handleAnalyzeKeyDown}
                        placeholder="Type in a prompt or select a suggested prompt..."
                        rows={3}
                        disabled={analyzing || !selectedAssetId}
                        className="w-full rq-input-dark rounded-lg px-4 py-3 pr-12 text-sm placeholder-[var(--rq-text-soft)] resize-none disabled:opacity-50"
                      />
                      <button
                        onClick={() => handleAnalyzeSubmit()}
                        disabled={analyzing || !prompt.trim() || !selectedAssetId || !selectedModel}
                        className="absolute bottom-3 right-3 p-2 rounded-lg bg-[var(--btn)] hover:bg-[var(--btn-hover)] text-white disabled:opacity-30 disabled:hover:bg-[var(--btn)] transition-colors"
                        title="Send prompt"
                      >
                        {analyzing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right column — Suggested prompts */}
                <div className="lg:col-span-3">
                  <div className="rq-card p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-[var(--rq-magenta)]" />
                      <h3 className="text-sm font-medium text-[var(--rq-text-soft)]">Try our suggested prompts</h3>
                    </div>
                    <div className="space-y-2">
                      {SUGGESTED_PROMPTS.map((sp, i) => {
                        const isThemes = sp.prompt === THEMES_PROMPT
                        const disabled = isThemes
                          ? themesLoading || projectIds.length === 0
                          : analyzing || !selectedAssetId || !selectedModel
                        return (
                          <button
                            key={i}
                            onClick={() => handleSuggestedPrompt(sp.prompt)}
                            disabled={disabled}
                            className="w-full text-left px-4 py-3 bg-black/20 hover:bg-white/5 border border-white/10 hover:border-[var(--brand-light)]/40 rounded-lg text-sm text-[var(--rq-text-soft)] transition-all disabled:opacity-40 disabled:hover:bg-black/20 disabled:hover:border-white/10"
                          >
                            {sp.prompt}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Results area */}
              {analyzing && (
                <div className="rq-card p-8 text-center">
                  <Loader2 className="w-8 h-8 text-[var(--rq-blue)] mx-auto mb-3 animate-spin" />
                  <p className="text-[var(--rq-text-soft)] text-sm">Analyzing video...</p>
                  <p className="text-[var(--rq-text-soft)] text-xs mt-1">Processing time depends on provider and model.</p>
                </div>
              )}

              {result && !analyzing && (
                <div className="rq-card overflow-hidden">
                  {/* Result header */}
                  <div className="px-5 py-3 bg-black/20 border-b border-white/10 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--rq-magenta)] font-medium truncate">
                        Prompt: &ldquo;{result.prompt}&rdquo;
                      </div>
                      <div className="text-xs text-[var(--rq-text-soft)] mt-0.5">
                        Asset: {result.asset_filename}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--rq-text-soft)] flex-shrink-0 ml-4">
                      {result.provider && <span>{result.provider}</span>}
                      {result.model_used && <span>{result.model_used}</span>}
                      <span>{result.tokens_used.toLocaleString()} tokens</span>
                      <span>{result.processing_time}s</span>
                      <button
                        onClick={() => handleAnalyzeSubmit(result.prompt)}
                        disabled={analyzing || !selectedAssetId || !selectedModel}
                        className="p-1.5 rounded-md hover:bg-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                        title="Re-run this prompt"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Result body */}
                  <div className="p-5">
                    <div className="text-sm text-[var(--rq-text-soft)] whitespace-pre-wrap leading-relaxed">
                      {result.response}
                    </div>
                  </div>
                </div>
              )}

              {/* Themes clustering results */}
              {themesLoading && (
                <div className="rq-card p-8 text-center">
                  <Loader2 className="w-8 h-8 text-[var(--rq-blue)] mx-auto mb-3 animate-spin" />
                  <p className="text-[var(--rq-text-soft)] text-sm">Clustering analyzed clips into themes...</p>
                </div>
              )}

              {!themesLoading && themesMessage && themeVideos.every(v => v.themes.length === 0) && (
                <div className="rq-card p-5">
                  <p className="text-sm text-[var(--rq-text-soft)]">{themesMessage}</p>
                </div>
              )}

              {!themesLoading && themeVideos.some(v => v.themes.length > 0) && (
                <div className="space-y-8">
                  {themeVideos.map(video => (
                    <div key={video.asset_id} className="space-y-3">
                      {/* Per-video header — every theme below belongs to this one source. */}
                      <div className="flex items-baseline gap-2 px-1">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--rq-text)]">
                          {video.asset_filename || `Asset ${video.asset_id}`}
                        </span>
                        <span className="text-[10px] text-[var(--rq-text-soft)]/60 tabular-nums">
                          {video.themes.length} theme{video.themes.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {video.themes.length === 0 && video.unclustered_reason && (
                        <div className="rq-card p-4">
                          <p className="text-xs text-[var(--rq-text-soft)]">{video.unclustered_reason}</p>
                        </div>
                      )}

                      <div className="space-y-4">
                        {video.themes.map((theme, idx) => (
                          <div key={`${video.asset_id}-${idx}`} className="rq-card overflow-hidden">
                            <div className="px-5 py-3 bg-black/20 border-b border-white/10 flex items-center justify-between">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-[var(--rq-text)] truncate">
                                  {theme.label}
                                </div>
                                <div className="text-xs text-[var(--rq-text-soft)] mt-0.5">
                                  {theme.intent} · {theme.moment_count} clip{theme.moment_count !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {theme.moments.map(m => (
                                <div
                                  key={m.id}
                                  className="rq-card overflow-hidden hover:border-white/25 transition-all"
                                >
                                  <div className="relative aspect-video bg-black/35">
                                    {m.thumbnail_url ? (
                                      <img
                                        src={m.thumbnail_url}
                                        alt={m.title || 'Clip thumbnail'}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Film className="w-8 h-8 text-[var(--rq-text-soft)]" />
                                      </div>
                                    )}
                                    {m.start_time !== null && m.end_time !== null && (
                                      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                                        <Clock className="w-3 h-3" />
                                        {formatTime((m.end_time || 0) - (m.start_time || 0))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-2.5">
                                    <p className="text-xs text-[var(--rq-text-soft)] line-clamp-2 leading-relaxed">
                                      {m.visual_description || m.title || 'No description'}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </div>
  )
}
